import {
    Component,
    AfterViewInit,
    ChangeDetectorRef,
    HostListener,
    inject,
    ChangeDetectionStrategy,
    OnDestroy,
} from '@angular/core';
import { confirm } from 'devextreme/ui/dialog';
import moment from 'moment';
import {
    debounce as _debounce,
    DebouncedFunc,
    each as _each,
    get as _get,
    includes as _includes,
    toNumber as _toNumber,
    toString as _toString,
} from 'lodash';
import {
    ThAddressSuggestorService,
    PrintSheetService,
    RenderPersistReportService,
    NotificationService,
    UtilService,
    MessageType,
} from 'src/app/shared/services';
import {
    ALL_ENTITIES,
    entityById,
    SellerEntity,
} from 'src/app/shared/config/entity-config';
import { DeliveryNoteParameter } from 'src/app/shared/services/print-sheet/interfaces/delivery-note-param';
import { RenderRequest } from 'src/app/shared/services/print-sheet/interfaces/report-api-request';
import { environment } from 'src/environments/environment';
import { branchList } from 'src/app/shared/config/branch-list';
import { MessageService } from './services/message.service';
import {
    DeliveryNoteInput,
    getInitialDeliveryNoteInput,
    inputMomentFormat,
} from './models/delivery-note-input';
import { buildDeliveryNoteParam } from './models/build-delivery-note-param';
import { DxLoadPanelComponent } from 'devextreme-angular/ui/load-panel';
import { DxBoxComponent } from 'devextreme-angular/ui/box';
import {
    DxiItemComponent,
    DxoPagingComponent,
    DxoEditingComponent,
    DxoRowDraggingComponent,
    DxiColumnComponent,
} from 'devextreme-angular/ui/nested';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import { DxDateBoxComponent } from 'devextreme-angular/ui/date-box';
import { DxSwitchComponent } from 'devextreme-angular/ui/switch';
import { NgClass } from '@angular/common';
import { DxSelectBoxComponent } from 'devextreme-angular/ui/select-box';
import { DxAutocompleteComponent } from 'devextreme-angular/ui/autocomplete';
import { DxTextAreaComponent } from 'devextreme-angular/ui/text-area';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';

@Component({
    selector: 'app-temp-delivery-note',
    templateUrl: './temp-delivery-note.component.html',
    styleUrls: ['./temp-delivery-note.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxLoadPanelComponent,
        DxBoxComponent,
        DxiItemComponent,
        DxButtonComponent,
        DxTextBoxComponent,
        DxDateBoxComponent,
        DxSwitchComponent,
        NgClass,
        DxSelectBoxComponent,
        DxAutocompleteComponent,
        DxTextAreaComponent,
        DxDataGridComponent,
        DxoPagingComponent,
        DxoEditingComponent,
        DxoRowDraggingComponent,
        DxiColumnComponent,
    ],
})
export class TempDeliveryNoteComponent implements AfterViewInit, OnDestroy {
    private cdref = inject(ChangeDetectorRef);
    private thAddressSuggestor = inject(ThAddressSuggestorService);
    private printSheetService = inject(PrintSheetService);
    private renderPersistService = inject(RenderPersistReportService);
    private messageService = inject(MessageService);
    private utilService = inject(UtilService);
    private notificationService = inject(NotificationService);

    branchList = branchList;
    sellerEntities = ALL_ENTITIES;

    postCodeSuggestions: any[] = [];
    provinceSuggestions: any[] = [];
    districtSuggestions: any[] = [];
    subDistrictSuggestions: any[] = [];

    shareData: {
        fileName: string;
        sharedUrl: string;
    } | null = null;
    isExporting = false;
    messageSourceWindow: Window | null = null;

    inputValues: DeliveryNoteInput = getInitialDeliveryNoteInput();
    onInputsUpdated: DebouncedFunc<() => void>;

    private previousSuggestionSelection = '';

    constructor() {
        this.onInputsUpdated = _debounce(this.internalOnInputsUpdated, 200);
        this.onReorder = this.onReorder.bind(this);
    }

    @HostListener('window:message', ['$event'])
    onMessage(event: any): void {
        const responseStatus: string = _get(event, 'data.status');
        if (responseStatus === 'ready') {
            this.messageSourceWindow = event.source;
        } else if (responseStatus === 'data' && event.data.data) {
            this.messageService.fillInputFromMessage(
                event.data.data,
                this.inputValues,
            );
            this.notificationService.pushMessage({
                message: 'Lnwshop data is loaded',
                messageType: MessageType.success,
            });
        } else if (responseStatus === 'error') {
            this.notificationService.pushMessage({
                message: `Lnwshop Data loading failed with responsed error: ${event.data.message}`,
                messageType: MessageType.error,
            });
        }
    }

    requestMessage(): void {
        if (this.messageSourceWindow) {
            console.log('posted message');
            this.messageSourceWindow.postMessage(
                {
                    status: 'request',
                },
                '*',
            );
        } else {
            console.error('No connection');
        }
    }

    ngAfterViewInit(): void {
        this.postCodeSuggestions = this.thAddressSuggestor.getAllZipCodes();
        this.provinceSuggestions = this.thAddressSuggestor.getAllProvinces();
        this.fillInitialValues(this.inputValues);
        this.cdref.detectChanges();
    }

    ngOnDestroy(): void {
        // A trailing debounced call would run detectChanges() on the destroyed
        // view — navigating away mid-edit is enough to trigger it.
        this.onInputsUpdated.cancel();
    }

    clearForm(): void {
        confirm('Filled data will be reset.', 'Confirm Clear Data ?').then(
            (result: boolean) => {
                if (result) {
                    this.inputValues = getInitialDeliveryNoteInput();
                    this.fillInitialValues(this.inputValues);
                    this.notificationService.pushMessage({
                        message: 'Form is cleared.',
                        messageType: MessageType.info,
                    });
                }
            },
        );
    }

    exportPdf(): void {
        if (!this.isExporting) {
            this.internalOnInputsUpdated();
            const seller = this.sellerOrNotify();
            if (!seller) {
                return;
            }
            const request = buildDeliveryNoteParam(this.inputValues, seller);
            let tabTitle = 'ใบส่งของชั่วคราว';
            if (request.orderInfo.invNum) {
                tabTitle += ` ${request.orderInfo.invNum}`;
            }
            this.isExporting = true;
            this.printSheetService
                .printDeliveryNote([request], tabTitle)
                .subscribe(
                    () => {
                        this.notificationService.pushMessage({
                            message: 'Delivery note is successfully exported.',
                            messageType: MessageType.success,
                        });
                    },
                    (error) => {
                        console.error(error);
                        this.notificationService.pushMessage({
                            message: this.utilService.errorToString(error),
                            messageType: MessageType.error,
                        });
                        // Also here, not only in complete: RxJS never calls
                        // complete after an error, and the pop-up-blocked
                        // preview tells the user to print again — which the
                        // load panel would otherwise make impossible.
                        this.isExporting = false;
                    },
                    () => {
                        this.isExporting = false;
                    },
                );
        }
    }

    saveAndShare(): void {
        if (!this.isExporting) {
            this.internalOnInputsUpdated();
            // Before the guard: a rejected save must not leave the previous
            // document's share URL on screen next to the new form, or the
            // wrong link gets copied to a customer.
            this.shareData = null;
            const seller = this.sellerOrNotify();
            if (!seller) {
                return;
            }
            const request = buildDeliveryNoteParam(this.inputValues, seller);
            const payload: RenderRequest<DeliveryNoteParameter> = {
                data: { pages: [request] },
                template: {
                    name: environment.deliveryNoteTemplateName,
                },
            };
            this.isExporting = true;
            this.renderPersistService
                .renderAndGetSharedUrl<DeliveryNoteParameter>({
                    renderRequest: payload,
                    reportStoreRequest: {
                        fileName: request.orderInfo.invNum
                            ? `${request.orderInfo.invNum}.pdf`
                            : 'untitled.pdf',
                        parents: [environment.tempDeliveryNoteDriveFolderId],
                    },
                })
                .subscribe(
                    (response) => {
                        this.shareData = {
                            fileName: response.name,
                            sharedUrl: response.webViewLink,
                        };
                        this.notificationService.pushMessage({
                            message: `${response.name} shared URL is now available.`,
                            messageType: MessageType.success,
                        });
                        this.isExporting = false;
                    },
                    (error) => {
                        console.error(error);
                        this.notificationService.pushMessage({
                            message: this.utilService.errorToString(error),
                            messageType: MessageType.error,
                        });
                        this.isExporting = false;
                    },
                );
        }
    }

    openSharedUrl(): void {
        const sharedUrl = _get(this.shareData, 'sharedUrl');
        if (sharedUrl) {
            window.open(sharedUrl, '_blank');
        }
    }

    copySharedUrlToClipboard(): void {
        const sharedUrl = _get(this.shareData, 'sharedUrl');
        if (sharedUrl) {
            navigator.clipboard.writeText(sharedUrl).then(
                () => {
                    this.notificationService.pushMessage({
                        message: 'URL copied to clipboard.',
                        messageType: MessageType.success,
                        displayTime: 100,
                    });
                },
                (e) => {
                    console.error(e);
                },
            );
        }
    }

    onSaved(): void {
        this.generateItemOrderNumber();
        this.onInputsUpdated();
    }

    onReorder(e: any): void {
        const visibleRows = e.component.getVisibleRows();
        const toIndex = this.inputValues.items.indexOf(
            visibleRows[e.toIndex].data,
        );

        const fromIndex = this.inputValues.items.indexOf(e.itemData);

        this.inputValues.items.splice(fromIndex, 1);
        this.inputValues.items.splice(toIndex, 0, e.itemData);
        this.generateItemOrderNumber();
    }

    private sellerOrNotify(): SellerEntity | null {
        const entityId = this.inputValues.docInfo.entityId;
        if (!entityId) {
            this.notificationService.pushMessage({
                message: 'กรุณาเลือกผู้ขายก่อนออกเอกสาร',
                messageType: MessageType.error,
            });
            return null;
        }
        return entityById(entityId);
    }

    private fillInitialValues(input: DeliveryNoteInput): void {
        input.docInfo.date = moment().format(inputMomentFormat);
        input.docInfo.isSigned = true;
        this.shareData = null;
    }

    private internalOnInputsUpdated(): void {
        this.suggestAddressIfNeeded();
        this.constructFullAddress();
        this.cdref.detectChanges();
    }

    private suggestAddressIfNeeded(): void {
        const selectedPostCode =
            _toString(this.inputValues.customerInfo.postCode) || '';
        const selectedProvince = this.inputValues.customerInfo.province || '';
        const selectedDistrict = this.inputValues.customerInfo.district || '';

        const currentSuggestionSelection =
            selectedPostCode + selectedProvince + selectedDistrict;

        if (this.previousSuggestionSelection !== currentSuggestionSelection) {
            this.previousSuggestionSelection = currentSuggestionSelection;
            if (selectedPostCode.length === 5) {
                const selectedPostCodeNum = _toNumber(selectedPostCode);
                const province: string =
                    this.thAddressSuggestor.getProvinceByPostCode(
                        selectedPostCodeNum,
                    );
                this.provinceSuggestions = [province];

                if (selectedProvince) {
                    const districts: string[] =
                        this.thAddressSuggestor.getDistrictsByPostCode(
                            selectedPostCodeNum,
                        );

                    this.districtSuggestions = districts;
                } else {
                    this.districtSuggestions = [];
                }

                if (selectedDistrict) {
                    const subDistricts: string[] =
                        this.thAddressSuggestor.getSubDistrictsByDistrict(
                            selectedPostCodeNum,
                            selectedDistrict,
                        );

                    this.subDistrictSuggestions = subDistricts;
                } else {
                    this.subDistrictSuggestions = [];
                }
            } else {
                this.provinceSuggestions =
                    this.thAddressSuggestor.getAllProvinces();
            }
        }
    }

    private constructFullAddress(): void {
        const address1 = this.inputValues.customerInfo.address1?.trim();
        const subDistrict = this.inputValues.customerInfo.subDistrict?.trim();
        const district = this.inputValues.customerInfo.district?.trim();
        const province = this.inputValues.customerInfo.province?.trim();
        const postCode = _toString(
            this.inputValues.customerInfo.postCode,
        ).trim();
        let result = '';
        if (address1) {
            result += address1;
        }
        const isBangkok = _includes(province, 'กรุงเทพ');
        if (subDistrict) {
            result += isBangkok ? ' แขวง' : ' ตำบล';
            result += subDistrict;
        }
        if (district) {
            result += isBangkok ? ' เขต' : ' อำเภอ';
            result += district;
        }
        if (province) {
            result += ` จังหวัด${province}`;
        }
        if (postCode) {
            result += ` ${postCode}`;
        }
        result = result.trim();
        this.inputValues.customerInfo.fullAddress = result;
    }

    private generateItemOrderNumber(): void {
        _each(this.inputValues.items, (item, index) => {
            item.itemNo = index + 1;
        });
    }
}
